import { useState } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ChevronUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { IconBox } from '@/components/IconBox'
import { navConfig, type NavItem } from './nav-config'

export function LayoutNav() {
  const { t } = useTranslation()
  const location = useLocation()
  const { isMobile } = useBreakpoint()
  const { isNavToggle, setNavToggle } = useUIStore()
  const [toggledMenus, setToggledMenus] = useState<Record<string, boolean>>({})

  const isActive = (item: NavItem): boolean => {
    if (location.pathname === item.path) return true
    if (item.children?.some((child) => location.pathname === child.path)) return true
    return false
  }

  const isChildActive = (child: NavItem): boolean => {
    return location.pathname === child.path
  }

  const isMenuOpen = (item: NavItem): boolean => {
    if (toggledMenus[item.label] !== undefined) return toggledMenus[item.label]
    return true
  }

  const handlePrimaryClick = (item: NavItem) => {
    if (item.children && item.children.length > 0) {
      if (isNavToggle) {
        setNavToggle(false)
        setToggledMenus((prev) => ({ ...prev, [item.label]: true }))
      } else {
        setToggledMenus((prev) => ({ ...prev, [item.label]: !isMenuOpen(item) }))
      }
    }
  }

  const handleChildClick = () => {
    if (isMobile) setNavToggle(true)
  }

  return (
    <>
      <nav
        className={cn(
          'fixed left-0 top-[56px] md:top-[80px] z-40 bg-white border-r-2 border-[#f3f3f3] flex flex-col select-none transition-all duration-300 overflow-hidden',
          isMobile
            ? isNavToggle ? 'w-0' : 'w-[210px] h-full'
            : isNavToggle
              ? 'w-[58px] h-[calc(100vh-80px)]'
              : 'w-[240px] h-[calc(100vh-80px)]',
        )}
      >
        <ul className="flex flex-col flex-1 overflow-y-auto w-full">
          {navConfig.filter((item) => item.enabled).map((item) => (
            <li key={item.label}>
              {item.children && item.children.length > 0 ? (
                <div
                  onClick={() => handlePrimaryClick(item)}
                  className={cn(
                    'flex items-center h-[56px] px-[14px] cursor-pointer font-semibold text-[14px] text-nav relative transition-colors',
                    'hover:bg-[#edf5ed] hover:text-[#4ea95c] [&:hover_svg]:text-[#4ea95c]',
                    isActive(item) && 'text-[#4ea95c] [&_svg]:text-[#4ea95c]',
                  )}
                >
                  <div className="mr-2 shrink-0">
                    <item.icon className="h-6 w-6" />
                  </div>
                  {!isNavToggle && (
                    <>
                      <span className="font-semibold">{t(`nav.${item.label}`)}</span>
                      <div
                        className={cn(
                          'absolute right-[30px] top-0 bottom-0 flex items-center transition-transform duration-300',
                          isActive(item) && 'text-primary',
                          !isMenuOpen(item) && 'rotate-180',
                        )}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  onClick={handleChildClick}
                  className={cn(
                    'flex items-center h-[56px] px-[14px] cursor-pointer font-semibold text-[14px] text-nav transition-colors',
                    'hover:bg-[#edf5ed] hover:text-[#4ea95c] [&:hover_svg]:text-[#4ea95c]',
                    isActive(item) && 'text-[#4ea95c] [&_svg]:text-[#4ea95c]',
                  )}
                >
                  <div className="mr-2 shrink-0">
                    <item.icon className="h-6 w-6" />
                  </div>
                  {!isNavToggle && (
                    <span className="font-semibold">{t(`nav.${item.label}`)}</span>
                  )}
                </Link>
              )}

              {item.children && !isNavToggle && (
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300',
                    isMenuOpen(item) ? 'max-h-[500px]' : 'max-h-0',
                  )}
                >
                  {item.children.map((child) => (
                    <Link
                      key={child.label}
                      to={child.path}
                      onClick={handleChildClick}
                      className={cn(
                        'flex items-center h-[56px] pl-[46px] text-[14px] text-nav cursor-pointer transition-colors',
                        'hover:text-[#4ea95c] hover:bg-[#edf5ed]',
                        isChildActive(child) && 'text-[#4ea95c] bg-[#edf5ed]',
                      )}
                    >
                      {t(`nav.${child.label}`)}
                    </Link>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>

        {!isNavToggle && (
          <div className="py-4">
            <IconBox />
          </div>
        )}
      </nav>

      {isMobile && !isNavToggle && (
        <div
          className="fixed inset-0 z-30 bg-[rgba(145,157,166,0.45)]"
          onClick={() => setNavToggle(true)}
        />
      )}
    </>
  )
}
